import { mobileXXLargeMax, tabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import styled, { css } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { MAX_WIDTH } from '../layout/PageWrapper.style';

export const Container = styled.section<{ floorPlans?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;

  @media print {
    height: auto !important;

    [data-slider-rail='true'] {
      transform: none !important;
    }

    [data-slider-slide='true'] {
      display: none !important;
    }

    [data-print-active='true'] {
      display: block !important;

      img {
        width: 700px !important;
        height: auto !important;
      }
    }
  }
`;

export const ImageBox = styled.div<{ panAndZoom?: boolean; $mode: 'single' | 'auto' }>`
  position: relative;
  cursor: pointer;
  user-select: none;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    max-width: 100vw;
    height: 100%;
    width: ${({ $mode }) => ($mode === 'single' ? '100%' : 'auto')};
    -webkit-user-drag: none;
  }

  .full-screen-img {
    object-fit: contain;
    max-width: 100%;
    width: auto;
  }

  .standard-img {
    object-fit: ${({ $mode }) => ($mode === 'single' ? 'cover' : 'contain')};
  }

  ${({ panAndZoom }) =>
    !panAndZoom
      ? css`
          overflow: hidden;
        `
      : ''}
`;

export const ImageOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 20px;

  img {
    width: 180px;
    height: 30%;
    max-height: 180px;
  }

  p {
    margin-top: 30px;
    font-size: 20px;
    color: ${(props) => props.theme.white};
    text-align: center;
  }

  @media (${mobileXXLargeMax}) {
    svg {
      width: 70px;
      height: 70px;
    }

    p {
      font-size: 16px;
      margin-top: 15px;
    }
  }
`;

export const ArrowButton = styled.button<{ side: 'right' | 'left'; sitemapTouch?: boolean }>`
  position: absolute;
  top: 0;
  width: 100px;
  z-index: 1;

  /**
    * Prevent arrow taking up full height on sitemap image so user can magnify that area
    * still center arrow on sitemap image when height
  */
  ${({ sitemapTouch }) =>
    sitemapTouch
      ? `
        transform: translate(0, -50%); 
        top: 50%; 
        width: 50px;
      `
      : `
        top: 0;
        width: 100px;
        height: 100%;
      `}

  ${(props) => props.side === 'right' && 'right: 0;'}
  ${(props) => props.side === 'left' && 'left: 0;'}
  
  & svg {
    filter: drop-shadow(0px 0px 6px rgb(0 0 0 / 0.5));
  }

  @media print {
    display: none;
  }
`;

export const CounterContainer = styled.div<{ center?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;

  ${(props) =>
    props.center &&
    css`
      max-width: ${MAX_WIDTH}px;
      margin: 0 auto;
    `}

  @media print {
    display: none;
  }
`;

export const PhotoCounter = styled.div<{ $alt?: boolean }>`
  background-color: ${({ $alt }) => ($alt ? `rgba(53, 53, 53, 0.5)` : `rgba(0, 0, 0, 0.7)`)};
  display: flex;
  flex-direction: row;
  padding: ${({ $alt }) => ($alt ? `10px` : `4px 6px`)};
  border-radius: 4px;
  position: absolute;
  bottom: ${({ $alt }) => ($alt ? 35 : 10)}px;
  ${({ $alt }) =>
    $alt
      ? css`
          right: 30px;
          font-size: 15px;
          font-weight: bold;
        `
      : css`
          left: 30px;
        `}
  z-index: 10;

  @media (${tabletMax}) {
    ${({ $alt }) =>
      $alt
        ? css`
            right: 10px;
            bottom: 25px;
          `
        : css`
            left: 10px;
          `}
  }
`;

export const ZoomContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;

  @media print {
    display: none;
  }
`;

export const ZoomControls = styled.div`
  background-color: ${({ theme }) => theme.white};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 15px 5px;
  border-radius: 8px;
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
  min-width: 145px;
  font-size: 14px;

  p {
    user-select: none;
  }
`;

export const ZoomControl = styled(FontAwesomeIcon)<{ $isDisabled: boolean }>`
  width: 35px;
  ${({ $isDisabled, theme }) =>
    $isDisabled
      ? css`
          cursor: not-allowed;
          pointer-events: none;
          color: ${theme.lightGrey};
        `
      : css`
          cursor: pointer;
        `};
`;

export const PanoramicIconBox = styled.div`
  position: absolute;
  right: 5px;
  bottom: 30px;
`;

export const NoImageBackground = styled.div`
  background-color: ${(props) => props.theme.backgroundLighter};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 800px;
  height: 100%;
  align-self: center;

  img {
    margin-top: 30px;
    width: 300px;
    height: 200px;
  }

  & > p {
    color: ${(props) => props.theme.backgroundLight};
    margin-top: 30px;
    font-size: 22px;
  }
`;

export const Curtain = styled.div`
  display: none;
  position: absolute;
  z-index: 5;
  top: 0;
  width: 25px;
  height: 100%;

  @media (pointer: coarse) {
    display: block;
  }
`;
